import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { darfSehen, sidebarGroups, routeById, tabsFor } from '../../navigation/routes';
import { hatRecht } from '../../berechtigungen';
import type { Recht } from '../../berechtigungen';
import { Icon } from '../icons';

/* Der CommandItem-Typ liegt im gespiegelten Kit; hier nur die Felder, die
   die Palette tatsaechlich liest, damit der Import ohne Pfad-Alias auskommt. */
export interface AppCommand {
  id: string;
  label: string;
  hint?: string;
  group?: string;
  keywords?: string[];
  icon?: React.ReactNode;
  perform: () => void;
}

interface ActionDef {
  id: string;
  label: string;
  routeId: string;
  keywords?: string[];
  /**
   * Recht fuer die Aktion selbst, zusaetzlich zum Recht der Seite: wer eine
   * Seite nur lesen darf, bekommt "... anlegen" nicht angeboten (die Seite
   * zeigt den Knopf dann auch nicht).
   */
  recht?: Recht;
}

const ACTIONS: ActionDef[] = [
  { id: 'act-new-post', label: 'Neuen LinkedIn-Beitrag erstellen', routeId: 'li-post', keywords: ['neu', 'beitrag', 'post'], recht: 'linkedin.bearbeiten' },
  { id: 'act-new-idea', label: 'Beitragsidee hinzufügen', routeId: 'li-ideas', keywords: ['neu', 'idee'], recht: 'linkedin.bearbeiten' },
  { id: 'act-generate', label: 'Text generieren', routeId: 'text', keywords: ['ki', 'text'], recht: 'inhalte.bearbeiten' },
  { id: 'act-new-company', label: 'Firma anlegen', routeId: 'companies', keywords: ['neu', 'firma'], recht: 'vertrieb.bearbeiten' },
  { id: 'act-profile', label: 'Mein Profil', routeId: 'profile', keywords: ['profil', 'passwort'] },
];

/**
 * Eintraege fuer die Befehlspalette: jede sichtbare Seite plus ein paar
 * Aktionen. Alles navigiert nur -- die Palette selbst ist kontextfrei.
 * Seiten und Aktionen, deren Recht der Person fehlt, tauchen nicht auf
 * (sidebarGroups, tabsFor und darfSehen mit dem Benutzer; Aktionen
 * zusaetzlich ueber ihr eigenes `recht`).
 */
export function useAppCommands(): AppCommand[] {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return useMemo(() => {
    const nav: AppCommand[] = [];
    for (const { group, items } of sidebarGroups(user)) {
      // Jeder Sidebar-Eintrag plus seine Reiter (Seiten mit `tabOf`), soweit das Recht reicht.
      for (const item of items.flatMap((i) => tabsFor(i.id, user))) {
        nav.push({
          id: `nav-${item.id}`,
          label: item.label,
          hint: group.label ?? undefined,
          group: 'Navigation',
          keywords: [...(item.keywords ?? []), item.description ?? ''].filter(Boolean),
          icon: item.icon ? React.createElement(Icon, { name: item.icon, size: 16 }) : undefined,
          perform: () => navigate(item.path),
        });
      }
    }

    const actions: AppCommand[] = ACTIONS.flatMap((a) => {
      const route = routeById(a.routeId);
      if (!route || !darfSehen(user, route) || (a.recht && !hatRecht(user, a.recht))) return [];
      return [{
        id: a.id,
        label: a.label,
        group: 'Aktionen',
        hint: route.label,
        keywords: a.keywords,
        icon: React.createElement(Icon, { name: 'arrow-right', size: 16 }),
        perform: () => navigate(route.path),
      }];
    });

    actions.push({
      id: 'act-logout',
      label: 'Abmelden',
      group: 'Aktionen',
      keywords: ['logout', 'abmelden'],
      icon: React.createElement(Icon, { name: 'logout', size: 16 }),
      perform: () => {
        logout().then(() => navigate('/login')).catch(() => navigate('/login'));
      },
    });

    return [...nav, ...actions];
  }, [navigate, user, logout]);
}
